import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

const routes: RouteRecordRaw[] = [
  { path: '/', redirect: '/dashboard' },
  { path: '/dashboard', component: () => import('@/features/dashboard/DashboardPage.vue'), meta: { permission: 'menu.dashboard' } },
  // 1.1.79: 仓点总览（第二块仪表盘，按 SPU 展开 SKU）
  {
    path: '/warehouse-overview',
    component: () => import('@/features/replenishment/WarehouseOverviewPage.vue'),
    meta: { permission: 'menu.warehouse_overview' },
  },
  { path: '/skus', component: () => import('@/features/skus/SkusPage.vue'), meta: { permission: 'menu.skus' } },
  { path: '/inventory', component: () => import('@/features/inventory/InventoryPage.vue'), meta: { permission: 'menu.inventory' } },
  { path: '/inventory/age', component: () => import('@/features/inventory/AgePage.vue'), meta: { permission: 'menu.age' } },
  { path: '/inventory/transit', component: () => import('@/features/inventory/TransitPage.vue'), meta: { permission: 'menu.transit' } },
  { path: '/inventory/factory-orders', component: () => import('@/features/inventory/FactoryOrdersPage.vue'), meta: { permission: 'menu.factory_orders' } },
  { path: '/inventory/factory-shippable', component: () => import('@/features/inventory/FactoryShippablePage.vue'), meta: { permission: 'menu.factory_shippable' } },
  { path: '/warehouses', component: () => import('@/features/inventory/WarehousesPage.vue'), meta: { permission: 'menu.warehouses' } },
  { path: '/sales', component: () => import('@/features/sales/SalesPage.vue'), meta: { permission: 'menu.sales' } },
  { path: '/sales/analysis', component: () => import('@/features/sales/SalesAnalysisPage.vue'), meta: { permission: 'menu.sales_analysis' } },
  { path: '/replenishment', component: () => import('@/features/replenishment/ReplenishmentPage.vue'), meta: { permission: 'menu.replenishment' } },
  {
    path: '/replenishment/settings',
    component: () => import('@/features/replenishment/ReplenishmentSettingsPage.vue'), meta: { permission: 'button.replenishment.settings' },
  },
  { path: '/ratios', component: () => import('@/features/config/RatiosPage.vue'), meta: { permission: 'menu.ratios' } },
  { path: '/sku-groups', component: () => import('@/features/config/GroupsPage.vue'), meta: { permission: 'menu.sku_groups' } },
  { path: '/activities', component: () => import('@/features/config/ActivitiesPage.vue'), meta: { permission: 'menu.activities' } },
  { path: '/permissions', component: () => import('@/features/permissions/PermissionsPage.vue'), meta: { permission: 'menu.permissions' } },
  { path: '/users', component: () => import('@/features/config/UsersPage.vue'), meta: { permission: 'menu.users' } },
  { path: '/audit', component: () => import('@/features/config/AuditPage.vue'), meta: { permission: 'menu.audit' } },
  // 1.1.85: 「我的权限」—— 普通用户也能看（无 meta.permission，AppShell 不会对它做权限拦截），
  //   让运营 / 备货专员随时自查"我是谁、我能看什么、我有什么权限、怎么申请"。
  { path: '/my-permissions', component: () => import('@/features/permissions/MyPermissionsPage.vue') },
  { path: '/help', component: () => import('@/features/config/HelpPage.vue'), meta: { permission: 'menu.help' } },
  { path: '/warehouse-analysis', component: () => import('@/features/analysis/WarehouseAnalysisPage.vue'), meta: { permission: 'menu.warehouse_analysis' } },
  { path: '/:pathMatch(.*)*', redirect: '/dashboard' },
];

export const router = createRouter({ history: createWebHistory(), routes });

/* ===== 1.1.66: 懒加载 chunk 加载失败的自愈 =====
   🔴 场景（用户实际踩到过）：部署换了一版前端后，**没刷新页面**的那台电脑上，
   点菜单「没反应」—— 有按下去的动效但页面不跳转。
   原因链：本页所有路由都是 `() => import(...)`（各自一个带内容 hash 的 chunk）→
   部署时 `pnpm build` 的 clean 清空了 dist，**旧 hash 文件没了** →
   旧页面去加载旧 chunk → 后端 catch-all 把找不到的资源**回落成 index.html（HTML）** →
   浏览器按 JS 解析 HTML → 动态 import 失败 → 而这里原本没有任何兜底 → 静默失败。

   自愈方式：整页刷新。静态资源都是 `Cache-Control: no-store`，刷新**必定拿到新版**。

   ⚠️ 必须防死循环：万一刷新后仍然失败（真的坏了 / 服务器 404 了），10 秒内不再重复刷，
   否则会把用户浏览器刷成无限重载。 */
const CHUNK_RELOAD_GUARD_KEY = 'chunk_reload_at';
const CHUNK_RELOAD_MIN_GAP_MS = 10_000;
function isChunkLoadError(error: unknown): boolean {
  const message = String((error as Error)?.message || error || '');
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Expected a JavaScript module script|MIME type of "text\/html"|Unable to preload/i.test(
    message,
  );
}
router.onError((error) => {
  if (!isChunkLoadError(error)) return;
  let lastReloadAt = 0;
  try {
    lastReloadAt = Number(window.sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) || 0);
  } catch {
    /* 隐私模式下 sessionStorage 可能不可用，退化为不防抖 */
  }
  if (Date.now() - lastReloadAt < CHUNK_RELOAD_MIN_GAP_MS) return;
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    /* 同上 */
  }
  window.location.reload();
});
